// Cliente HTTP de la nomina (PP-22), mismo patron que sites/http.ts: token en
// el header, sin cookies ni redirecciones, y toda respuesta por el parser de
// @ucorp/contracts antes de tocar estado. Los cuerpos que salen tambien pasan
// por el parser: lo que no cumple el contrato no viaja.
//
// El listado pide includeInactive=true: la baja de la nomina se muestra como
// fila con inRoster false, no desaparece, y desde ahi se reincorpora.
//
// Aislamiento entre proveedores: la garantia es de la API (filtra por la
// habilitacion de la cuenta). Aca se repite en el cliente como defensa: una
// fila de otro proveedor nunca llega a la pantalla, aunque la respuesta la
// traiga.
import {
  CreateDriverRequest,
  CreateVehicleRequest,
  Driver,
  DriverListResponse,
  ProviderRosterResponse,
  SiteVehicle,
  SubcontractorListResponse,
  UpdateDriverRequest,
  UpdateVehicleRequest,
  VehicleListResponse,
} from "@ucorp/contracts";
import { createFleetCall, parseOrFail, sitePath } from "./call.js";
import { createHttpDocumentsPort } from "./documents/http.js";
import { FleetError, type FleetPort, type FleetSite } from "./port.js";

export function createHttpFleetPort(apiUrl: string, request: typeof fetch = fetch): FleetPort {
  const call = createFleetCall(apiUrl, request);
  const site = sitePath;
  const listQuery = (s: FleetSite) => `?providerId=${encodeURIComponent(s.providerId)}&includeInactive=true`;
  const vehiclePath = (s: FleetSite, id: string) =>
    `${site(s)}/vehicles/${encodeURIComponent(s.providerId)}/${encodeURIComponent(id)}`;
  const driverPath = (s: FleetSite, id: string) =>
    `${site(s)}/drivers/${encodeURIComponent(s.providerId)}/${encodeURIComponent(id)}`;

  /** Una respuesta de alta, edicion o baja de otro proveedor es una respuesta rota. */
  function own<T extends { providerId: string }>(s: FleetSite, row: T): T {
    if (row.providerId !== s.providerId) throw new FleetError("unavailable");
    return row;
  }

  return {
    ...createHttpDocumentsPort(call),
    async listVehicles(session, s) {
      const data = parseOrFail(VehicleListResponse, await call(session, "GET", `${site(s)}/vehicles${listQuery(s)}`), "unavailable");
      return { today: data.today, items: data.vehicles.filter((v) => v.providerId === s.providerId) };
    },
    async listDrivers(session, s) {
      const data = parseOrFail(DriverListResponse, await call(session, "GET", `${site(s)}/drivers${listQuery(s)}`), "unavailable");
      return { today: data.today, items: data.drivers.filter((d) => d.providerId === s.providerId) };
    },
    async listSubcontractors(session, s) {
      const path = `${site(s)}/providers/${encodeURIComponent(s.providerId)}/subcontractors`;
      const data = parseOrFail(SubcontractorListResponse, await call(session, "GET", path), "unavailable");
      if (data.providerId !== s.providerId) throw new FleetError("unavailable");
      return data.subcontractors;
    },
    async createVehicle(session, s, body) {
      const payload = parseOrFail(CreateVehicleRequest, { ...body, providerId: s.providerId }, "validation");
      return own(s, parseOrFail(SiteVehicle, await call(session, "POST", `${site(s)}/vehicles`, payload), "unavailable"));
    },
    async updateVehicle(session, s, vehicleId, body) {
      const payload = parseOrFail(UpdateVehicleRequest, body, "validation");
      return own(s, parseOrFail(SiteVehicle, await call(session, "PATCH", vehiclePath(s, vehicleId), payload), "unavailable"));
    },
    async deactivateVehicle(session, s, vehicleId) {
      return own(s, parseOrFail(SiteVehicle, await call(session, "DELETE", vehiclePath(s, vehicleId)), "unavailable"));
    },
    async reinstateVehicle(session, s, vehicleId) {
      return own(s, parseOrFail(SiteVehicle, await call(session, "POST", `${vehiclePath(s, vehicleId)}/reinstate`), "unavailable"));
    },
    async createDriver(session, s, body) {
      const payload = parseOrFail(CreateDriverRequest, { ...body, providerId: s.providerId }, "validation");
      return own(s, parseOrFail(Driver, await call(session, "POST", `${site(s)}/drivers`, payload), "unavailable"));
    },
    async updateDriver(session, s, driverId, body) {
      const payload = parseOrFail(UpdateDriverRequest, body, "validation");
      return own(s, parseOrFail(Driver, await call(session, "PATCH", driverPath(s, driverId), payload), "unavailable"));
    },
    async deactivateDriver(session, s, driverId) {
      return own(s, parseOrFail(Driver, await call(session, "DELETE", driverPath(s, driverId)), "unavailable"));
    },
    async reinstateDriver(session, s, driverId) {
      return own(s, parseOrFail(Driver, await call(session, "POST", `${driverPath(s, driverId)}/reinstate`), "unavailable"));
    },
    // PP-70: sin planta en el path ni en la query; la habilitacion ajena que
    // llegara igual se descarta en logic/roster.ts (allSitesRows).
    async listRoster(session) {
      return parseOrFail(ProviderRosterResponse, await call(session, "GET", "/provider/roster"), "unavailable");
    },
  };
}
